import { useMemo } from 'react';
import { LookupCombo } from '../../../components/LookupCombo';

/**
 * A header lookup drawn as legacy draws it — the shared multi-column
 * `<LookupCombo>` (XAF ASPxGridLookup) with a filter row under the captions —
 * keyed by Oid, because the names it shows repeat in this data (several clients
 * are called "City of Houston", one per site).
 *
 * `cells(option)` gives the columns in display order; column 0 is what the
 * closed field shows.
 */
export function QuoteGridLookup<T extends { id: string }>({
  options, value, cols, cells, onPick, required = false, disabled = false, label,
  popupWidth = 640, colWidths, emptyLabel,
}: {
  options: T[];
  value: string | null;
  cols: string[];
  cells: (option: T) => Array<string | null | undefined>;
  onPick: (id: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  /** Accessible name — the field caption sits in a sibling cell. */
  label: string;
  popupWidth?: number;
  colWidths?: (number | string)[];
  /** The closed field's text when the value is not in `options` (e.g. a
   *  record the list no longer offers). */
  emptyLabel?: string | null;
}) {
  // One string[] per option; the Map is keyed by the ARRAY so a row maps back to
  // its Oid even when two rows read the same.
  const { lookup, idByRow, nameById } = useMemo(() => {
    const rows: string[][] = [];
    const ids = new Map<string[], string>();
    const names = new Map<string, string>();
    for (const o of options) {
      const row = cells(o).map((c) => c ?? '');
      rows.push(row);
      ids.set(row, o.id);
      names.set(o.id.toLowerCase(), row[0] ?? '');
    }
    return { lookup: { cols, rows }, idByRow: ids, nameById: names };
  }, [options, cols, cells]);

  const key = value ? value.toLowerCase() : '';
  const shown = key ? (nameById.get(key) ?? emptyLabel ?? '') : '';

  return (
    <span className="qt-lookup">
      <LookupCombo
        lookup={lookup}
        ariaLabel={label}
        value={key}
        rowKey={(row) => (idByRow.get(row) ?? '').toLowerCase()}
        labelText={shown}
        plainValue
        disabled={disabled}
        placeholder=""
        columnFilters
        showFilter={false}
        popupWidth={popupWidth}
        colWidths={colWidths}
        wrapCells
        naOption={!required}
        onClear={() => onPick(null)}
        className={required && !value ? 'required-empty' : undefined}
        onPick={(row) => onPick(idByRow.get(row) ?? null)}
      />
    </span>
  );
}
